import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { IDEA_CATEGORIES } from '../../types/ideaBoard';
import type { IdeaCategory } from '../../types/ideaBoard';
import { useSubmitIdea } from '../../hooks/useIdeaBoard';

const MIN_CHARS = 10;
const MAX_CHARS = 5000;

interface SubmitIdeaModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const SubmitIdeaModal: React.FC<SubmitIdeaModalProps> = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [ideaText, setIdeaText] = useState('');
  const [category, setCategory] = useState<IdeaCategory | ''>('');
  const [validationError, setValidationError] = useState('');

  const submitMutation = useSubmitIdea();
  const { reset: resetSubmitMutation } = submitMutation;

  // Clear form whenever the dialog opens
  useEffect(() => {
    if (open) {
      setIdeaText('');
      setCategory('');
      setValidationError('');
      resetSubmitMutation();
    }
  }, [open, resetSubmitMutation]);

  const handleClose = () => {
    if (!submitMutation.isPending) {
      onClose();
    }
  };

  const validate = (): boolean => {
    if (ideaText.trim().length < MIN_CHARS) {
      setValidationError(`Your idea must be at least ${MIN_CHARS} characters.`);
      return false;
    }
    if (ideaText.trim().length > MAX_CHARS) {
      setValidationError(`Your idea must not exceed ${MAX_CHARS} characters.`);
      return false;
    }
    setValidationError('');
    return true;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    submitMutation.mutate(
      {
        originalText: ideaText.trim(),
        category: category || null,
      },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
      }
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      aria-labelledby="submit-idea-dialog-title"
    >
      <DialogTitle id="submit-idea-dialog-title" sx={{ pb: 1 }}>Submit an Idea</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Share your ideas to improve MarketCompass. Your submission may be rewritten for clarity,
          but the essence of your idea will be preserved.
        </Typography>

        {submitMutation.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitMutation.error?.message || 'Failed to submit your idea. Please try again.'}
          </Alert>
        )}

        <TextField
          label="Describe your idea"
          multiline
          rows={5}
          fullWidth
          value={ideaText}
          onChange={(e) => {
            setIdeaText(e.target.value);
            if (validationError) setValidationError('');
          }}
          error={!!validationError}
          helperText={
            validationError ||
            `${ideaText.length.toLocaleString()} / ${MAX_CHARS.toLocaleString()} characters`
          }
          inputProps={{ maxLength: MAX_CHARS }}
          disabled={submitMutation.isPending}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth disabled={submitMutation.isPending}>
          <InputLabel id="category-label">Category (optional)</InputLabel>
          <Select
            labelId="category-label"
            value={category}
            label="Category (optional)"
            onChange={(e) => setCategory(e.target.value as IdeaCategory | '')}
          >
            <MenuItem value="">
              <em>Auto-detect</em>
            </MenuItem>
            {IDEA_CATEGORIES.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Leaving category blank will have AI automatically detect the most appropriate category.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitMutation.isPending || ideaText.trim().length < MIN_CHARS}
          startIcon={submitMutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {submitMutation.isPending ? 'Submitting...' : 'Submit Idea'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitIdeaModal;
